/* eslint-disable react/no-children-prop */
import { memo, useContext, useState } from "react";
import PropTypes from "prop-types";
import PassengersCounterDisplay from "app/pages/.shared/PassengersCounterDisplay";
import { FormattedDate, FormattedMessage, FormattedPlural } from "react-intl";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import TravelDatesContainer from "app/pages/.shared/TravelDates/TravelDatesContainer";
import BookingBannerPlaceholderContainer from "app/pages/Booking/BookingBannerPlaceholder/BookingBannerPlaceholderContainer";
import Collapse from "app/components/ui/collapse/Collapse";
import MyFlightDetail from "app/pages/Booking/MyFlightDetail";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import AppGlobalsContext from "app/AppGlobalsContext";
import OccupanciesDisplayLabel from "app/pages/.shared/OccupanciesDisplayLabel/OccupanciesDisplayLabel";
import NegociatedItem from "app/pages/FicheProduit/NegociatedList/NegociatedItem";
import "./HolidaySummary.scss";
import {
	AB_TEST_HOLIDAY_FLIGHT_SUMMARY_OPEN,
	FLAGSHIP_EVENT_KPIS,
	NEGOCIATED_TYPES,
	OFFER_TYPES,
	PRODUCT_EXPERIENCES,
	QUOTATION_CODE_STATUS,
} from "app/constants";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import classNames from "classnames";
import IconFlight from "app/pages/.shared/IconFlight";
import isEmpty from "lodash/isEmpty";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { EventCategory, HitType, useFlagship, useFsFlag } from "@flagship.io/react-sdk";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import ProductSummaryContainer from "app/pages/.shared/ProductSummary/ProductSummaryContainer";
import { parseDepartureCityLabel } from "app/utils/utils";
import IconCalendar from "app/pages/.shared/IconCalendar";
import { addDays } from "date-fns";
import AccommodationSummaryList from "app/pages/HotelOnly/components/AccommodationSummaryList/AccommodationSummaryList";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import AirlineLogo from "app/pages/.shared/AirlineLogo";
import IconCircuit from "app/pages/.shared/static/icons/WebsiteTunnelTypes/IconCircuit";
import { ArrowDownIcon } from "app/icons/navigation";
import { MultiUserFilledIcon } from "app/icons/profile";
import { HotelFilledIcon } from "app/icons/travel";
import { TransferFilledIcon } from "app/icons/navigation";
import { ICON_SIZES } from "app/constants";

const getUniqueAirlineCodes = flight => {
	if (!flight) {
		return [];
	}
	const codes = new Set();
	[flight.outbound, flight.inbound].forEach(direction => {
		if (direction?.legs) {
			direction.legs.forEach(leg => {
				if (leg?.airline?.code) {
					codes.add(leg.airline.code);
				}
			});
		}
	});
	return [...codes];
};

const IMG_SIZES_DESKTOP = {
	xlarge: { height: 201, width: 348 },
	large: { height: 201, width: 348 },
};

const IMG_SIZES_MOBILE = {
	medium: { width: 800 },
	small: { width: 800 },
};

const NEGOS_TO_EXCLUDE = [
	NEGOCIATED_TYPES.SPECIAL_OFFER,
	NEGOCIATED_TYPES.ACCOMMODATION,
	NEGOCIATED_TYPES.BOARD,
	NEGOCIATED_TYPES.CAR_RENTAL,
	NEGOCIATED_TYPES.TRANSFER,
	NEGOCIATED_TYPES.FLIGHT,
];

const COLLAPSE_EVENTS = {
	QUOTE: {
		FLIGHT: {
			OPEN: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_QUOTE_HS_FLIGHT_OPEN,
			CLOSE: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_QUOTE_HS_FLIGHT_CLOSE,
		},
		HOTEL: {
			OPEN: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_QUOTE_HS_HOTEL_OPEN,
			CLOSE: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_QUOTE_HS_HOTEL_CLOSE,
		},
	},
	PAYMENT: {
		FLIGHT: {
			OPEN: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_PAYMENT_HS_FLIGHT_OPEN,
			CLOSE: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_PAYMENT_HS_FLIGHT_CLOSE,
		},
		HOTEL: {
			OPEN: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_PAYMENT_HS_HOTEL_OPEN,
			CLOSE: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_PAYMENT_HS_HOTEL_CLOSE,
		},
	},
};

const HolidaySummary = ({
	isStopover,
	isSDP,
	isHotelOnly,
	booking = {},
	ficheProduit = {},
	showProductPicture,
	showBookingBanner,
	luggageMaxCount = 0,
	quotationStatus,
	negotiatedOfSelectedOffer = [],
	activities = [],
	flightOptions = {},
	accommodations = [],
	isQuotePage,
	isPaymentPage,
	destinations = [],
	departureCities = [],
}) => {
	const { sendHits } = useFlagship();
	const [flightCollapseActiveKey, setFlightCollapseActiveKey] = useState([]);

	const flag = useFsFlag("holiday_flight_summary_open")?.getValue(
		AB_TEST_HOLIDAY_FLIGHT_SUMMARY_OPEN.ORIGINAL
	);
	const openFlightSummary =
		{
			[AB_TEST_HOLIDAY_FLIGHT_SUMMARY_OPEN.ORIGINAL]: false,
			[AB_TEST_HOLIDAY_FLIGHT_SUMMARY_OPEN.OPEN_FROM_QUOTE]: isQuotePage || isPaymentPage,
			[AB_TEST_HOLIDAY_FLIGHT_SUMMARY_OPEN.OPEN_FROM_PAYMENT]: isPaymentPage,
		}[flag] ?? false;

	let productTypeMessage = "booking.holiday.summary.sdp.to.title";

	if (isStopover) {
		productTypeMessage = "booking.holiday.summary.stopover.to.title";
	} else if (isHotelOnly) {
		productTypeMessage = "booking.holiday.summary.hotel.only.to.title";
	}

	const { track } = useAnalytics();

	const transfer = booking.transfer || {};

	// from /booking/restoreSession, we only have departureCity.code
	let departureCityLabel = "";

	if (booking?.departureCity?.label) {
		departureCityLabel = booking?.departureCity?.label;
	} else if (booking?.departureCity?.code) {
		departureCityLabel = departureCities.find(
			city => city?.code === booking?.departureCity?.code
		)?.label;

		// fallback to code if label not found (for the case vers the payment email link still redirect
		// to /booking/restoreSession instead of /sdp/booking/restoreSession for SDP bookings)
		if (!departureCityLabel) {
			departureCityLabel = booking?.departureCity?.code;
		}
	}

	let destinationCity = "";
	// from /booking/restoreSession, we only have destinationResort.code
	if (ficheProduit?.productExperience === PRODUCT_EXPERIENCES.CIRCUIT) {
		destinationCity = ficheProduit?.country;
	} else if (booking?.destinationResort?.label) {
		destinationCity = booking?.destinationResort?.label;
	} else if (booking?.destinationResort?.code) {
		destinationCity = destinations.find(dest => dest?.code === booking?.destinationResort?.code)
			?.label;
	} else if (ficheProduit?.destination?.label) {
		destinationCity = ficheProduit?.destination?.label;
	} else if (ficheProduit?.resort) {
		destinationCity = ficheProduit?.resort;
	}

	const tripAdvisorData = ficheProduit.tripAdvisor || {};
	const holidayCheckData = ficheProduit.holidaycheck || {};

	const { resolution } = useContext(AppGlobalsContext);

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const photo = ficheProduit.photos && ficheProduit.photos[0];

	// pour pp, les negotiated sont par type d'offre alors que pour sdp, non
	let negotiatedList = [];

	const { stopOverCity, stopOverDuration } = booking?.flight?.outbound || {};
	const airlineCodes = getUniqueAirlineCodes(booking.flight);

	if (negotiatedOfSelectedOffer?.length > 0) {
		negotiatedList = negotiatedOfSelectedOffer;
	} else if (ficheProduit?.negotiated?.length > 0) {
		negotiatedList = ficheProduit.negotiated;
	}

	const negotiatedListToDisplay =
		(negotiatedList &&
			negotiatedList.filter(negotiated => {
				return !NEGOS_TO_EXCLUDE.includes(negotiated?.type);
			})) ||
		[];

	const holidaySummaryDetailsClass = classNames("holiday-summary__details", {
		"holiday-summary__details--loading": quotationStatus !== QUOTATION_CODE_STATUS.SUCCESS,
	});

	const accommodationDetailsClassName = classNames(
		"holiday-summary__collapse-content holiday-summary__accommodation-details",
		{
			"holiday-summary__accommodation-details--multiple-accommodations":
				accommodations.length > 1,
		}
	);

	const nonIncludedActivitiesList = activities.filter(activity => {
		return activity.guests.every(guest => guest.includedGuest === 0);
	});

	const productName = (
		<ProductNameWithCategory name={ficheProduit.name} category={ficheProduit.category} />
	);

	const sendCollapseEvent = (section, isOpen) => {
		const pageType = (isQuotePage && "QUOTE") || (isPaymentPage && "PAYMENT");
		const userAction = isOpen ? "OPEN" : "CLOSE";

		if (!pageType || !userAction) {
			return;
		}

		const action = COLLAPSE_EVENTS[pageType][section][userAction];

		if (action) {
			sendHits({
				type: HitType.EVENT,
				category: EventCategory.ACTION_TRACKING,
				action,
			});

			track(action?.toLowerCase());
		}
	};

	const handleFlightCollapseChange = data => {
		const open = data.length > 0;
		setFlightCollapseActiveKey(data);
		sendCollapseEvent("FLIGHT", open);
	};

	const handleHotelCollapseChange = data => {
		const open = data.length > 0;
		sendCollapseEvent("HOTEL", open);
	};

	const isFlightCollapseExpanded = flightCollapseActiveKey.length > 0;

	const startDate = new Date(booking?.departureDate);
	const endDate = addDays(new Date(booking?.departureDate), booking?.duration?.value);
	const shouldShowStartYear = startDate.getFullYear() !== endDate.getFullYear();
	const shouldDisplayTravelDates = booking?.offer?.type === OFFER_TYPES.ACCOMODATION_ONLY;

	return (
		<div className="holiday-summary" data-testid="holiday-summary">
			{showBookingBanner && <BookingBannerPlaceholderContainer />}

			<div className={holidaySummaryDetailsClass}>
				{showProductPicture && photo && (
					<div className="holiday-summary__picture">
						{isMobile ? (
							<BackgroundProgressiveImg
								sizes={IMG_SIZES_MOBILE}
								src={photo.url}
								alt={photo.alt}
								thumbnail={photo.thumbnail}
								backgroundPosition={"center"}
								productUri={ficheProduit?.uri}
							/>
						) : (
							<ProgressiveImg
								sizes={IMG_SIZES_DESKTOP}
								src={photo.url}
								alt={photo.alt}
								resolution={resolution}
								thumbnail={photo.thumbnail}
								productUri={ficheProduit?.uri}
							/>
						)}
					</div>
				)}

				<div className="holiday-summary__title" data-testid="detail-resort">
					<Typography variant={TYPOGRAPHY_VARIANTS.XL2} isBold>
						<FormattedMessage id={productTypeMessage} />
					</Typography>
				</div>

				<ul className="holiday-summary__list">
					{shouldDisplayTravelDates && (
						<li className="holiday-summary__list-item">
							<div className="holiday-summary__list-item-head">
								<IconCalendar />
								<Typography
									variant={TYPOGRAPHY_VARIANTS.LARGE}
									isBold
									className="holiday-summary__list-item-head-content"
									component="p"
								>
									<FormattedDate
										timeZone="UTC"
										value={startDate}
										weekday="short"
										day="2-digit"
										month="short"
										year={shouldShowStartYear ? "numeric" : undefined}
									/>{" "}
									-{" "}
									<FormattedDate
										timeZone="UTC"
										value={endDate}
										weekday="short"
										day="2-digit"
										month="short"
										year="numeric"
									/>
								</Typography>
							</div>
						</li>
					)}

					<li className="holiday-summary__list-item" data-testid="detail-travellers">
						<div className="holiday-summary__list-item-head">
							<MultiUserFilledIcon className="icon" />
							{accommodations.length > 0 ? (
								<Typography
									variant={TYPOGRAPHY_VARIANTS.LARGE}
									isBold
									className="holiday-summary__travellers"
									component="p"
								>
									{isSDP ? (
										<OccupanciesDisplayLabel
											occupancies={booking.occupancies}
											showPrefix={false}
										/>
									) : (
										<PassengersCounterDisplay
											adults={booking.adults}
											children={booking.children}
											infants={booking.infants}
										/>
									)}
									<span>/</span>
									<FormattedPlural
										value={accommodations.length}
										one={
											<FormattedMessage
												values={{ count: accommodations.length }}
												id="general.room.count.label.singular"
											/>
										}
										other={
											<FormattedMessage
												values={{ count: accommodations.length }}
												id="general.room.count.label.plural"
											/>
										}
									/>
								</Typography>
							) : (
								<div className="holiday-summary__travellers-loader">
									<LoaderBar height={10} />
								</div>
							)}
						</div>
					</li>

					{departureCityLabel && (
						<li
							className="holiday-summary__list-item"
							data-testid="detail-departureCity"
						>
							<Collapse
								{...openFlightSummary && { defaultActiveKey: ["0"] }}
								className="holiday-summary__collapse"
								expandIcon={({ isActive }) => {
									return (
										<ArrowDownIcon
											style={{
												transform: `rotate(${isActive ? 180 : 0}deg)`,
											}}
										/>
									);
								}}
								onChange={handleFlightCollapseChange}
							>
								<Collapse.Panel
									headerClass="holiday-summary__collapse-header"
									header={
										<div className="holiday-summary__list-item-head">
											<IconFlight />
											<div className="holiday-summary__list-item-head-content">
												<Typography
													variant={TYPOGRAPHY_VARIANTS.LARGE}
													isBold
												>
													<FormattedMessage
														id="booking.holiday.summary.to.title"
														values={{
															departureCity: parseDepartureCityLabel(
																departureCityLabel
															),
															destinationCity: destinationCity,
														}}
														className="holiday-summary__departureCity"
													/>
												</Typography>

												<TravelDatesContainer
													showDepartureDateForInboundFlight
												/>

												{stopOverDuration &&
													stopOverCity &&
													!isFlightCollapseExpanded && (
														<div className="holiday-summary__stopover-duration">
															<Typography
																variant={TYPOGRAPHY_VARIANTS.SMALL}
																isBold
															>
																<FormattedPlural
																	value={stopOverDuration}
																	one={
																		<FormattedMessage
																			values={{
																				days: (
																					<FormattedMessage
																						values={{
																							duration: stopOverDuration,
																						}}
																						id="general.duration.days.singular"
																					/>
																				),
																			}}
																			id="stop.over.flight.duration.label"
																		/>
																	}
																	other={
																		<FormattedMessage
																			values={{
																				days: (
																					<FormattedMessage
																						values={{
																							duration: stopOverDuration,
																						}}
																						id="general.duration.days.plural"
																					/>
																				),
																			}}
																			id="stop.over.flight.duration.label"
																		/>
																	}
																/>
															</Typography>
														</div>
													)}

												{airlineCodes.length > 0 ? (
													<Typography
														variant={TYPOGRAPHY_VARIANTS.REGULAR}
														className={classNames(
															"holiday-summary__flight-operated-logo",
															{
																"holiday-summary__flight-operated-logo--hidden": isFlightCollapseExpanded,
															}
														)}
													>
														<FormattedMessage id="flight.operated.by.label" />
														{airlineCodes.map(code => (
															<AirlineLogo
																key={code}
																airlineCode={code}
																width={66}
																height={20}
																alt={code}
															/>
														))}
													</Typography>
												) : (
													!isFlightCollapseExpanded && (
														<div className="holiday-summary__flight-operated-logo-loader">
															<LoaderBar height={10} />
														</div>
													)
												)}
											</div>
										</div>
									}
								>
									<div className="holiday-summary__collapse-content">
										<MyFlightDetail
											flight={booking.flight}
											lugaggeCount={booking.luggage?.quantity}
											luggageMaxCount={luggageMaxCount}
											bookingCabinLuggage={booking?.cabinLuggage}
											flightOptions={flightOptions}
											maxWeightPerCheckedBaggage={
												booking?.luggage?.maxWeightPerBaggage
											}
										/>
									</div>
								</Collapse.Panel>
							</Collapse>
						</li>
					)}

					<li className="holiday-summary__list-item" data-testid="detail-product-name">
						<Collapse
							className="holiday-summary__collapse"
							expandIcon={({ isActive }) => {
								return (
									<ArrowDownIcon
										style={{
											transform: `rotate(${isActive ? 180 : 0}deg)`,
										}}
										data-testid="detail-product-head-icon"
									/>
								);
							}}
							onChange={handleHotelCollapseChange}
						>
							<Collapse.Panel
								headerClass="holiday-summary__collapse-header"
								header={
									<div className="holiday-summary__list-item-head">
										{[
											PRODUCT_EXPERIENCES.CIRCUIT,
											PRODUCT_EXPERIENCES.COMBINE,
											PRODUCT_EXPERIENCES.CRUISE,
											PRODUCT_EXPERIENCES.AUTOTOUR,
										].includes(ficheProduit?.productExperience) ? (
											<IconCircuit />
										) : (
											<HotelFilledIcon className="icon" />
										)}
										<div className="holiday-summary__list-item-head-content">
											<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
												{productName}
											</Typography>
											<ProductRatingContainer
												locationName={productName}
												tripAdvisor={tripAdvisorData}
												holidayCheck={{
													...holidayCheckData,
													size: "small",
												}}
											/>
											<ProductSummaryContainer />
										</div>
									</div>
								}
							>
								<div className={accommodationDetailsClassName}>
									{accommodations?.length > 0 && (
										<AccommodationSummaryList accommodations={accommodations} />
									)}

									{(transfer?.vehicule?.length > 0 ||
										negotiatedListToDisplay?.length > 0) && (
										<div className="holiday-summary__add-ons-and-negotiated">
											{transfer.vehicule && (
												<div className="holiday-summary__list-item-head holiday-summary__transfer-info">
													<TransferFilledIcon
														size={ICON_SIZES.XS}
														aria-hidden="true"
													/>
													<Typography
														variant={TYPOGRAPHY_VARIANTS.REGULAR}
														data-testid="detail-transfer"
														component="p"
													>
														{transfer.vehicule}
													</Typography>
												</div>
											)}

											{!isEmpty(nonIncludedActivitiesList) &&
												nonIncludedActivitiesList.map((activity, index) => {
													return (
														<div
															key={activity.name + index}
															className="holiday-summary__list-item-head"
															data-testid="activities-extra"
														>
															<NegociatedItem label={activity.name} />
														</div>
													);
												})}

											{negotiatedListToDisplay.length > 0 &&
												negotiatedListToDisplay.map((negotiated, index) => {
													return (
														<div
															key={negotiated.label + index}
															className="holiday-summary__list-item-head"
															data-testid="detail-negotiated"
														>
															<NegociatedItem
																label={negotiated.label}
															/>
														</div>
													);
												})}
										</div>
									)}
								</div>
							</Collapse.Panel>
						</Collapse>
					</li>
				</ul>
			</div>
		</div>
	);
};

HolidaySummary.propTypes = {
	isStopover: PropTypes.bool,
	isHotelOnly: PropTypes.bool,
	isSDP: PropTypes.bool,
	showProductPicture: PropTypes.bool,
	booking: PropTypes.object,
	ficheProduit: PropTypes.object,
	showBookingBanner: PropTypes.bool,
	luggageMaxCount: PropTypes.number,
	quotationStatus: PropTypes.string,
	negotiatedOfSelectedOffer: PropTypes.array,
	activities: PropTypes.arrayOf(PropTypes.object),
	flightOptions: PropTypes.object,
	priceChangeData: PropTypes.object,
	quotationCode: PropTypes.string,
	accommodations: PropTypes.array,
	isQuotePage: PropTypes.bool,
	isPaymentPage: PropTypes.bool,
	destinations: PropTypes.array,
	departureCities: PropTypes.array,
};

export default memo(HolidaySummary);
